// notifications.ts — TanStack Query hooks for the in-app bell "Notifications"
// feed (per-user + general dispensing-workflow events). Separate from inventory
// "Alerts" (derived stock alerts) which power the bell's second tab.
//
// Backed by /api/stocks/notifications/ (real API and, since the resupply
// deep-link work, an MSW handler too so the bell is demoable/E2E-testable). Legacy note:
// the hooks no-op (empty list) so the prototype keeps rendering.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch, apiFetchList } from '@/api/client';
import { useStore } from '@/store/store';

export type NotificationPriority = 'low' | 'normal' | 'high' | 'urgent';

export interface AppNotification {
  id: string;
  type: string;
  priority: NotificationPriority;
  title: string;
  message: string;
  prescriptionId: string | null;
  outwardId: string | null;
  /** Deep-link target: the backorder this event is about (resupply events). */
  backorderId: string | null;
  stage: string;
  createdAt: string;
  /** true when this is a store-wide notification (no single recipient) */
  general: boolean;
}

interface NotificationDto {
  id: string;
  recipient_id: string | null;
  outward: string | null;
  prescription_id: string | null;
  backorder_id?: string | null;
  notification_type: string;
  priority: NotificationPriority;
  title: string;
  message: string;
  stage: string;
  created_at: string;
}

function toUi(d: NotificationDto): AppNotification {
  return {
    id: d.id,
    type: d.notification_type,
    priority: d.priority,
    title: d.title,
    message: d.message,
    prescriptionId: d.prescription_id,
    outwardId: d.outward,
    backorderId: d.backorder_id ?? null,
    stage: d.stage ?? '',
    createdAt: d.created_at,
    general: d.recipient_id == null,
  };
}

const NOTIF_BASE = '/api/stocks/notifications/';

export const notificationKeys = {
  all: ['notifications'] as const,
  list: (store: string) => ['notifications', 'list', store] as const,
};

/** Current user's unread bell feed (targeted + general), store-scoped. Polls. */
export function useNotifications() {
  const { store } = useStore();
  const isValidUuid = /^[0-9a-f]{8}-/i.test(store);
  return useQuery({
    queryKey: notificationKeys.list(store),
    queryFn: async (): Promise<AppNotification[]> => {
      const rows = await apiFetchList<NotificationDto>(NOTIF_BASE, {
        query: { store_id: isValidUuid ? store : undefined },
      });
      return rows.map(toUi);
    },
    // keep the bell fresh without a manual refresh
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });
}

export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<unknown>(`${NOTIF_BASE}${id}/mark-read/`, { method: 'POST', body: {} }),
    onSuccess: () => qc.invalidateQueries({ queryKey: notificationKeys.all }),
  });
}

export function useMarkAllNotificationsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<unknown>(`${NOTIF_BASE}mark-all-read/`, { method: 'POST', body: {} }),
    onSuccess: () => qc.invalidateQueries({ queryKey: notificationKeys.all }),
  });
}
